import React from 'react'
import styled, { css } from 'styled-components'
import { useFormikContext } from 'formik'
import { Box, IconButton, Text, TextButton } from 'components'

// Utils and Services
import useWorkFlowLookup from 'hooks/lookup/use-workflow-lookup'
import useFormikError from 'hooks/use-formik-error'
import useBranch from 'hooks/use-branch'
import { removeLeadingZero } from 'utils/helper'
import { isLeadingOnlyOneZero } from 'utils/validation'

// Types
import { WidthProps } from 'types/styled'
import { LeadDetails } from 'types/global'

import MobileNumber from './MobileNumber'

type Props = {
  disabled?: boolean
  name: string
  width?: WidthProps
  isLoading?: boolean
}

const MobileNumberGroup: React.FC<Props> = ({ disabled = false, name, width = 'auto' }) => {
  const { contactInfoTypes } = useWorkFlowLookup({})
  const branch = useBranch()
  const { values, setFieldValue, setFieldTouched } = useFormikContext<LeadDetails.LeadDetailsForm>()
  const [getErrMsg] = useFormikError<LeadDetails.LeadDetailsForm>()
  const isDeleteDisabled = values.contactPerson.mobile.length === 1
  const mobiles = values.contactPerson.mobile

  const mobileContactInfoType =
    contactInfoTypes.find((item) => item.isDefault)?.id || contactInfoTypes.find((item) => item.name === 'Mobile')?.id

  const handleSetPrimary = (index: number) => {
    if (mobiles[index].isPrimary) return

    setFieldValue(
      name,
      mobiles.map((item, idx) => ({ ...item, isPrimary: idx === index })),
    )
  }

  const handleOnDelete = (idx: number) => {
    const isPrimaryEntry = mobiles[idx].isPrimary

    const filtered = mobiles.filter((_, index) => index !== idx)
    if (isPrimaryEntry) {
      filtered[0].isPrimary = true
    }

    setFieldValue(name, filtered)
  }

  return (
    <Container width={width}>
      <Text color={disabled ? 'gray4' : 'defaultText'} fontSize={14} lineHeight="24px" mb="4px">
        mobile_number_lbl
      </Text>
      {mobiles.map((mobile, idx) => {
        const fieldName = `${name}[${idx}].value`
        const errMsg = getErrMsg(fieldName as keyof LeadDetails.LeadDetailsForm)
        return (
          <Box display="flex" key={idx}>
            <FieldWrapper>
              <MobileNumber
                key={idx}
                disabled={disabled}
                error={errMsg}
                name={mobile.value}
                variant="simple"
                index={idx}
                value={mobile}
                onChange={(val) => {
                  setFieldValue(`${name}[${idx}]`, val)
                }}
                onBlur={() => {
                  if (isLeadingOnlyOneZero(String(mobile.number))) {
                    setFieldValue(`${name}[${idx}]`, {
                      ...mobile,
                      ...(mobile.number && { number: removeLeadingZero(String(mobile.number)) }),
                    })
                  }
                  setFieldTouched(`${name}[${idx}].value`, true)
                }}
              />
              {!disabled && (
                <ActionsContainer>
                  <MarkPrimaryIcon
                    icon={mobile.isPrimary ? 'flag' : ['far', 'flag']}
                    isPrimary={mobile.isPrimary}
                    onClick={() => handleSetPrimary(idx)}
                    tooltip="mark_as_primary_lbl"
                    tooltipPosition="bottom"
                    variant="ghost"
                  />
                  {!isDeleteDisabled && (
                    <DeleteIcon
                      disabled={isDeleteDisabled}
                      icon="trash-alt"
                      onClick={() => handleOnDelete(idx)}
                      variant="blandGhost"
                    />
                  )}
                </ActionsContainer>
              )}
            </FieldWrapper>
          </Box>
        )
      })}
      {!disabled && (
        <StyledTextButton
          icon="plus"
          inline
          onClick={() =>
            setFieldValue(`${name}`, [
              ...mobiles,
              {
                isPrimary: false,
                id: '',
                contactInfoTypeId: mobileContactInfoType,
                value: '',
                countryISO: branch.countryCode,
              },
            ])
          }
        >
          add_new_phone_btn
        </StyledTextButton>
      )}
    </Container>
  )
}

const Container = styled.div<{ width?: WidthProps }>`
  display: block;
  width: ${(p) => (p.width === 'auto' ? '100%' : `${p.width}px`)};
`

const FieldWrapper = styled.div`
  align-items: start;
  display: flex;
  width: 100%;
`

const ActionsContainer = styled.div`
  display: flex;
  padding-top: 1rem;
  padding-left: 0.5rem;
`

const actionButtonCss = css`
  padding: 2px 9px 5px;
`

const StyledTextButton = styled((p) => <TextButton {...p} />)`
  margin: 4px 0;
`

const DeleteIcon = styled(IconButton)`
  svg {
    color: ${({ theme }) => theme.colors.gray4};
    font-size: 15px;
  }

  ${actionButtonCss}
`

const MarkPrimaryIcon = styled(IconButton)<{ isPrimary?: boolean }>`
  ${actionButtonCss}

  svg {
    color: ${({ isPrimary, theme }) => (isPrimary ? theme.colors.orange : theme.colors.gray4)};
    font-size: 15px;
  }

  &:hover {
    svg {
      color: ${(p) => p.theme.colors.orange};
    }
  }
`

export default MobileNumberGroup
